<template>
  <v-dialog
    :value="visible"
    max-width="45rem"
    persistent
    scrollable
    @input="close"
  >
    <v-card :loading="loading" :disabled="loading">
      <v-card-title>{{ $t('pages.settings.users.editDialog.title') }}</v-card-title>

      <v-divider class="w-100" />

      <validation-observer ref="observer" v-slot="{ invalid }">
        <v-card-text v-if="user" class="pt-5">
          <validation-provider v-slot="{ errors }" name="firstName" rules="required">
            <v-text-field
              v-model="form.firstName"
              class="mb-2"
              outlined
              :error-messages="errors"
              autocomplete="off"
              name="editUserFirstName"
              :label="$t('pages.settings.users.addDialog.firstName')"
            />
          </validation-provider>

          <validation-provider v-slot="{ errors }" name="lastName" rules="required">
            <v-text-field
              v-model="form.lastName"
              class="mb-4"
              outlined
              :error-messages="errors"
              autocomplete="off"
              name="editUserLastName"
              :label="$t('pages.settings.users.addDialog.lastName')"
            />
          </validation-provider>

          <v-text-field
            :value="user.email"
            class="mb-4"
            outlined
            disabled
            hide-details
            :label="$t('pages.settings.users.addDialog.email')"
          >
            <template #append>
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    style="pointer-events: auto"
                    v-bind="attrs"
                    v-on="on"
                    @click="copyEmail"
                  >
                    {{ icons.mdiContentCopy }}
                  </v-icon>
                </template>
                <span>{{ $t('copyEmailAddress') }}</span>
              </v-tooltip>
            </template>
          </v-text-field>

          <v-divider class="mb-4" />

          <v-btn
            text
            small
            color="primary"
            class="mb-2"
            @click="showEmailFields = !showEmailFields"
          >
            {{ $t('pages.settings.users.editDialog.changeEmail') }}
          </v-btn>

          <template v-if="showEmailFields">
            <validation-provider v-slot="{ errors }" name="newEmail" rules="required|email">
              <v-text-field
                v-model="form.newEmail"
                class="mb-2"
                type="email"
                outlined
                :error-messages="errors"
                autocomplete="off"
                name="editUserNewEmail"
                :label="$t('fields.newEmail')"
              />
            </validation-provider>
            <validation-provider v-slot="{ errors }" name="confirmEmail" rules="required|confirmed:newEmail">
              <v-text-field
                v-model="form.confirmEmail"
                class="mb-2"
                type="email"
                outlined
                :error-messages="errors"
                autocomplete="off"
                name="editUserConfirmEmail"
                :label="$t('fields.confirmNewEmail')"
              />
            </validation-provider>
          </template>

          <v-divider class="my-4" />

          <v-btn
            text
            small
            color="primary"
            class="mb-2"
            @click="showPasswordFields = !showPasswordFields"
          >
            {{ $t('pages.settings.users.editDialog.changePassword') }}
          </v-btn>

          <template v-if="showPasswordFields">
            <validation-provider v-slot="{ errors }" name="newPassword" rules="required|min:6">
              <v-text-field
                v-model="form.newPassword"
                class="mb-2"
                type="password"
                outlined
                :error-messages="errors"
                autocomplete="new-password"
                name="editUserNewPassword"
                :label="$t('fields.newPassword')"
              />
            </validation-provider>
            <validation-provider v-slot="{ errors }" name="confirmPassword" rules="required|confirmed:newPassword">
              <v-text-field
                v-model="form.confirmPassword"
                outlined
                type="password"
                :error-messages="errors"
                autocomplete="new-password"
                name="editUserConfirmPassword"
                :label="$t('fields.confirmNewPassword')"
              />
            </validation-provider>
          </template>
        </v-card-text>

        <v-divider class="w-100" />

        <v-card-actions class="justify-end">
          <v-btn
            text
            color="error"
            :disabled="loading"
            @click="close"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :elevation="0"
            :disabled="loading || invalid"
            @click="save"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiContentCopy } from '@mdi/js'

export default {
  name: 'DialogEditUser',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    user: {
      validator: value => typeof value === 'object' || value === null,
      default: null
    }
  },

  data () {
    return {
      icons: { mdiContentCopy },
      loading: false,
      showEmailFields: false,
      showPasswordFields: false,
      form: {
        firstName: '',
        lastName: '',
        newEmail: '',
        confirmEmail: '',
        newPassword: '',
        confirmPassword: ''
      }
    }
  },

  computed: {
    visible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },

  watch: {
    visible (value) {
      if (value) {
        this.setUser()
      }
    }
  },

  methods: {
    setUser () {
      this.showEmailFields = false
      this.showPasswordFields = false

      const [firstName = '', lastName = ''] = (this.user && this.user.name ? this.user.name.split(' ') : [])

      this.form = {
        firstName,
        lastName,
        newEmail: '',
        confirmEmail: '',
        newPassword: '',
        confirmPassword: ''
      }

      if (this.$refs.observer) {
        this.$refs.observer.reset()
      }
    },

    async save () {
      const payload = {
        firstName: this.form.firstName,
        lastName: this.form.lastName
      }

      if (this.showEmailFields && this.form.newEmail) {
        payload.newEmail = this.form.newEmail
      }

      if (this.showPasswordFields && this.form.newPassword) {
        payload.newPassword = this.form.newPassword
      }

      try {
        this.loading = true
        await this.$http.put(`admin/settings/users/${this.user.id}`, payload)
        this.$notify({ title: this.$t('success'), message: this.$t('pages.settings.users.editDialog.updatedSuccess'), type: 'success' })
        this.$emit('finished')
        this.close()
        this.loading = false
      } catch (error) {
        this.loading = false

        if (error.response && error.response.status === 409) {
          this.$notify({ title: this.$t('error'), message: this.$t('pages.settings.users.editDialog.emailTaken'), type: 'error' })

          return
        }

        if (error.response && error.response.status === 404) {
          this.$notify({ title: this.$t('error'), message: this.$t('pages.settings.users.editDialog.userNotFound'), type: 'error' })
          this.$emit('finished')
          this.close()

          return
        }

        this.$notify({ title: this.$t('error'), message: this.$t('pages.settings.users.fetchFailed'), type: 'error' })
      }
    },

    copyEmail () {
      navigator.clipboard.writeText(this.user.email)
      this.$notify({ title: this.$t('success'), message: this.$t('copiedToClipboard'), type: 'success' })
    },

    close () {
      this.$emit('close')
    }
  }
}
</script>
